import type { JSX, Ref } from 'react';
import { Diamond } from '@gonfalon/icons/Diamond';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Focusable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import cx from 'clsx';

import styles from './Variation.module.css';

export type VariationProps = {
  value: string;
  name?: string;
  // May be undefined when a rule has no single variation; the marker is skipped when no color is given.
  color: string | undefined;
  description?: string;
  inlineDescription?: boolean;
  className?: string;
  variationDisplayClassName?: string;
  variationDisplayRef?: Ref<HTMLSpanElement>;
  onClick?(): void;
};

type VariationContentProps = VariationProps & {
  displayValue: string | JSX.Element;
};

function VariationContent({
  value,
  name,
  color,
  description,
  inlineDescription,
  className,
  variationDisplayClassName,
  variationDisplayRef,
  displayValue,
  onClick,
  ...props
}: VariationContentProps) {
  function onKeyDown(event: React.KeyboardEvent<HTMLSpanElement>) {
    if (event.key === 'Enter' && onClick) {
      onClick();
    }
  }

  const variationInfo = (
    <>
      {color && <Diamond className={styles.color} size="small" fill={color} />}
      <span
        ref={variationDisplayRef}
        data-test-id={`variation-display-${value}`}
        className={cx('Variation-display', variationDisplayClassName)}
      >
        {name || displayValue}
      </span>
    </>
  );

  if (!onClick && !description) {
    return <span className={className}>{variationInfo}</span>;
  }

  const hasTooltip = Boolean(description) && !inlineDescription;

  // We can't use react-aria's useButton here because it interferes with event propagation.
  // We're required to explicitly set tab-index, role, and onClick to ensure all child elements are clickable.
  const chip = (
    <span
      {...props}
      tabIndex={0}
      role="button"
      aria-label={msg('ui.variation.buttonLabel')}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className={cx(className, hasTooltip && styles.tooltipTarget)}
    >
      {variationInfo}
    </span>
  );

  if (hasTooltip) {
    return (
      <TooltipTrigger>
        <Focusable>{chip}</Focusable>
        <Tooltip placement="top">{description}</Tooltip>
      </TooltipTrigger>
    );
  }

  return (
    <>
      {chip}
      {inlineDescription && description ? <span>{description}</span> : null}
    </>
  );
}

export function Variation(props: VariationProps) {
  const { value, className } = props;
  const classes = cx(styles.variation, className);
  const displayValue =
    value === '' ? (
      <span className="u-very-subtle">
        <Message id="ui.variation.emptyString" />
      </span>
    ) : (
      value
    );
  return <VariationContent {...props} className={classes} displayValue={displayValue} />;
}
